/**
 * crateshelf.module.css — MY CRATE and Playlist. `docs/design-direction.md` §8.5.
 *
 * 45s standing in a box, seen from above and in front: two columns, each item a disc
 * circle dropped into a sleeve rectangle that covers it, alternating ±0.8° so the
 * crate is packed slightly unevenly, and deliberately uneven box heights **because
 * the metadata is uneven** — a real shelf is not a grid of identical tiles.
 *
 * WHAT IS NOT HERE, ON PURPOSE
 *
 * The two-column grid itself belongs to `states.module.css`'s `.shelf` frame, so the
 * loading, empty, error and loaded states occupy the *same* grid by construction
 * rather than by promise (§8.7–§8.10). The type tiers are the nine `.t-*` classes of
 * `type.css`, the halftone field and the printed rules are the global recipes in
 * `texture.css`, and the ring chips of the `A–Z / RECENT / RANDOM` switch are the
 * `TagChip` primitive. This module owns box model, hit slop and the two objects no
 * primitive already draws: the record-in-a-sleeve, and the `[ ]` filing bracket.
 *
 * THE RULES EVERY DECLARATION BELOW OBEYS
 *
 *  1. **Semantic aliases only** (§3.2) — no `--ink-*`, `--paper-*`, `--stock-*`,
 *     `--warm-*` or `--cool` is reachable from this file. `--plate-*` is not the raw
 *     palette: those are the per-track properties `plateCssVars()` writes onto one
 *     element, and they are the entire point of the plate system.
 *  2. **Radii are `var(--r-cut)` (0) or `var(--r-round)` (999px)** — nothing between
 *     (§5.3). The record and the bracket's hit ring are round; every printed edge is
 *     guillotine-cut.
 *  3. **Separation is a 1px printed rule, never a shadow** (§5.5). The only
 *     `box-shadow`s here are `--recess` and `--ink-offset`, both of which have a zero
 *     blur radius, and `--raised-hi`.
 *  4. **Every length that can be a token is a token** — `--s1 --s2 --s3 --s4 --s6
 *     --s8 --drop`. `font-size` never appears: type is set by the `.t-*` classes, and
 *     a local `font-size` would also opt the text out of §4.4's font-failure
 *     degradation.
 *  5. **Green does not exist** (§3.1, §12.1). No hue in 70–170° is reachable here.
 *  6. **Zero `mix-blend-mode`.** The blend budget is three per screen (§6.13) and the
 *     grain layer, the ghost and the plate art duotone already own it.
 *  7. **One inverted block per screen** (§5.5, §12.13). Exactly one rule in this file
 *     paints `--bg-paper` as a surface: `.playAll`. The sleeve is `--bg-paper-2`
 *     board stock seen edge-on, sized well under the 25% inversion cap.
 *
 * `prefers-reduced-motion` is handled globally by `motion.css`, which forces every
 * transition to 120ms and `transition-property: opacity`. Every transition below is
 * therefore written so the resting style is a finished style: with motion reduced the
 * record is simply already lifted, and the sleeve back is simply already showing.
 */

/* ────────────────────────────────────────────────────────────────────────────────
   Shared furniture.
   ──────────────────────────────────────────────────────────────────────────────── */

.sr {
  position: absolute;
  inline-size: 1px;
  block-size: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;
  border: 0;
}

/* Tracked Cyrillic caps read as shouting (§4.3, §12.16): a Cyrillic string keeps the
   mono tier and loses the tracking and the forced uppercase. */
:global(.t-mono).cyr {
  letter-spacing: 0.02em;
  text-transform: none;
}

.screen {
  display: grid;
  grid-template-columns: 1fr;
  align-content: start;
  gap: var(--s4);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The masthead — stencilled title, mono count/runtime, the sort switch (§8.5).
   ──────────────────────────────────────────────────────────────────────────────── */

.masthead {
  display: grid;
  gap: var(--s2);
  padding-inline: var(--gutter);
  padding-block-start: var(--s6);
}

.mastheadTitle {
  color: var(--text);
  margin: 0;
}

.mastheadMeta {
  color: var(--text-2);
}

/* The only place `--warn` appears outside the error plate (§8.9): a mutation that
   failed after the crate was already on screen is reported on one line rather than by
   replacing a crate the user can still use. */
.actionError {
  color: var(--danger-text);
}

/* 32px visual chips inside a 44px hit row (§5.6, §9.7), scrolling with a 16px mask
   fade at both ends so the row reads as continuing rather than ending. */
.switch {
  display: flex;
  gap: var(--s2);
  align-items: center;
  min-block-size: var(--drop);
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-mask-image: linear-gradient(
    90deg,
    transparent,
    #000 var(--s4),
    #000 calc(100% - var(--s4)),
    transparent
  );
  mask-image: linear-gradient(
    90deg,
    transparent,
    #000 var(--s4),
    #000 calc(100% - var(--s4)),
    transparent
  );
}

/* ────────────────────────────────────────────────────────────────────────────────
   The playlists ledger that sits above the crate.
   Hairline-ruled 44px rows, mono meta — no cards, no cover wall (§8.2, §12.22).
   ──────────────────────────────────────────────────────────────────────────────── */

.playlists {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.playlistRow {
  display: block;
  border-block-end: 1px solid var(--border);
}

.playlistRow:nth-child(4n) {
  border-block-end-color: var(--border-strong);
}

.playlistLink {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: center;
  gap: var(--s3);
  inline-size: 100%;
  min-block-size: var(--drop);
  padding-inline: var(--gutter);
  padding-block: var(--s2);
  border: 0;
  border-radius: var(--r-cut);
  background: transparent;
  color: var(--text);
  text-align: start;
  cursor: pointer;
}

.playlistLink:active {
  background: var(--bg-pressed);
}

.playlistMeta {
  color: var(--text-3);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The crate item: a record dropped into a sleeve (§8.5).
   ──────────────────────────────────────────────────────────────────────────────── */

.item {
  position: relative;
  display: block;
  /* ±0.8°, alternating, so the box is packed slightly unevenly. The rotation is a
     resting transform, not an animation: nothing moves here (§7.4). */
  transform: rotate(0.8deg);
}

.item:nth-child(even) {
  transform: rotate(-0.8deg);
}

.card {
  position: relative;
  display: block;
  /* The sleeve's minimum height is a fraction of the card's own *width* — the disc is
     sized in that unit too — and `cqi` is the only unit that can say so. See `.sleeve`. */
  container-type: inline-size;
}

/* The record. `touch-action: manipulation` is global; the long press below is a
   timer, not a drag, so this surface keeps the global value. */
.record {
  display: block;
  inline-size: 100%;
  padding: 0;
  border: 0;
  border-radius: var(--r-cut);
  background: transparent;
  color: var(--text);
  text-align: start;
  cursor: pointer;
}

.record:active .sleeve {
  background: var(--bg-pressed);
}

/*
 * Grooves, cut from a repeating gradient — they cost nothing and they are the reason
 * a 44px chip and a 140px record read as the same object (§6.4). The label is the
 * track's own hashed ink pair; a cover, when the channel supplied one, is composited
 * inside the label on Now Playing and nowhere else (§12.22).
 */
.disc {
  position: relative;
  display: block;
  inline-size: 82%;
  margin-inline-start: 9%;
  aspect-ratio: 1;
  /* The record is a printed object on the page ground, so it takes the ground step and
     the 1px rule every other object here takes (§5.5). Before this rule the grooves
     were drawn in `--bg` on a `--bg` page: the disc *was* the background, and on an
     OLED phone the whole top arc of the crate went missing. */
  border: 1px solid var(--border-strong);
  border-radius: var(--r-round);
  background: repeating-radial-gradient(
    circle at 50% 50%,
    var(--bg-panel) 0 1.5px,
    var(--border) 1.5px 2px,
    var(--bg-panel) 2px 3.5px
  );
  box-shadow: var(--recess);
  transition: transform var(--t8) var(--ease-cut);
}

/* The tap slides the record 12px up out of its sleeve (§8.5). It is a resting style
   of the now-playing item, not a one-shot: the record that is playing stays out. */
.record[data-lifted='true'] .disc {
  transform: translateY(-12px);
}

.disc::before,
.disc::after {
  content: '';
  position: absolute;
  border-radius: var(--r-round);
}

/* The label: 46% of the diameter, like a real 7". */
.disc::before {
  inset: 27%;
  background: var(--plate-bg);
  box-shadow: var(--recess);
}

/* One ink ring at 62% of the label — pattern, not image (§8.2). */
.disc::after {
  inset: 34%;
  border: 2px solid var(--plate-ink-1);
}

.spindle {
  position: absolute;
  inset-inline-start: 50%;
  inset-block-start: 50%;
  inline-size: 7%;
  aspect-ratio: 1;
  border-radius: var(--r-round);
  transform: translate(-50%, -50%);
  background: var(--bg);
}

/*
 * The sleeve. §8.5 gives the number verbatim — `margin-top: -38%` — so it is written
 * verbatim: the record drops into the board and only its arc stands proud of the
 * card stock.
 *
 * `--sleeve-lift` is set inline per item from the track's own plate seed, so the box
 * heights are uneven the way the metadata is uneven, and identically uneven on every
 * device. It is one of `--s1 --s2 --s3` or zero; nothing off the spacing scale
 * reaches this file.
 */
.sleeve {
  position: relative;
  display: grid;
  align-content: start;
  gap: var(--s1);
  margin-block-start: -38%;
  padding: var(--s3);
  padding-block-end: calc(var(--s3) + var(--sleeve-lift, 0px));
  /* §8.5: the sleeve covers the record's lower 62%. The disc is 82% of the card's
     width and the sleeve starts 38% up into it, so a sleeve shorter than 38% of that
     width leaves the record's bottom arc sticking out below the card stock. Short
     sleeves did exactly that; it was invisible only while the disc was the same colour
     as the page. */
  min-block-size: 38cqi;
  border: 1px solid var(--border);
  border-radius: var(--r-cut);
  background: var(--bg-paper-2);
  color: var(--text-on-paper);
}

.sleeveTitle {
  color: var(--text-on-paper);
}

.sleeveCat {
  color: var(--text-on-paper);
  opacity: 0.75;
}

/* ────────────────────────────────────────────────────────────────────────────────
   The sleeve back: a mono spec plate and a 1px-ruled action strip (§8.5).
   ──────────────────────────────────────────────────────────────────────────────── */

.back {
  display: grid;
  gap: var(--s2);
  margin-block-start: var(--s2);
  padding: var(--s3);
  border: 1px solid var(--border);
  border-radius: var(--r-cut);
  background: var(--bg-raised);
  box-shadow: var(--raised-hi);
}

.strip {
  display: grid;
  gap: 0;
}

/* Actions are words in square stamps — never an icon, never a trash can (§12.21). */
.stripButton {
  display: block;
  inline-size: 100%;
  min-block-size: var(--drop);
  padding-inline: var(--s2);
  border: 0;
  border-block-start: 1px solid var(--border);
  border-radius: var(--r-cut);
  background: transparent;
  color: var(--text);
  text-align: start;
  cursor: pointer;
}

.stripButton:first-child {
  border-block-start: 0;
}

.stripButton:active {
  background: var(--bg-pressed);
}

.stripButton[data-danger='true'] {
  color: var(--danger-text);
}

/* ────────────────────────────────────────────────────────────────────────────────
   The item's control row: the disclosure that opens the spec back, and the bracket.
   Both are 44px, and both are siblings of the record rather than nested inside it —
   a button inside a button is invalid HTML that browsers silently un-nest, and "play
   this" and "do something to this" are two actions that must be reachable separately
   (§9.3).
   ──────────────────────────────────────────────────────────────────────────────── */

.corner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-block-start: 1px solid var(--border);
}

.more {
  display: grid;
  place-items: center;
  inline-size: var(--drop);
  min-block-size: var(--drop);
  padding: 0;
  border: 0;
  border-radius: var(--r-cut);
  background: transparent;
  cursor: pointer;
}

/* Three dots in a column, drawn as a repeating gradient: no glyph, so it cannot fall
   back to a font that lacks it. The button's Russian name carries the meaning. */
.moreGlyph {
  display: block;
  inline-size: 3px;
  block-size: 15px;
  background: repeating-linear-gradient(to bottom, var(--text-2) 0 3px, transparent 3px 6px);
}

/* The filing bracket `[ ]` moved to `components.module.css` alongside
   `components/FileBracket.tsx`, so `TrackRow` can carry it — a record has to be
   fileable from the crate, not only from the shelf of records already filed. */

/* ────────────────────────────────────────────────────────────────────────────────
   Playlist — the ledger layout with the playlist header (§8.5, §8.6).
   ──────────────────────────────────────────────────────────────────────────────── */

.playlistHeader {
  display: grid;
  gap: var(--s2);
  padding-inline: var(--gutter);
  padding-block-start: var(--s6);
}

.renameForm {
  display: grid;
  grid-template-columns: 1fr auto;
  align-items: end;
  gap: var(--s3);
}

/* The chalk line of §8.3: a display-tier line sitting on a 1px rule that thickens to
   2px accent on focus. There is no box, because there is no search box anywhere in
   this app. */
.renameInput {
  inline-size: 100%;
  min-block-size: var(--drop);
  padding: 0;
  border: 0;
  border-block-end: 1px solid var(--border);
  border-radius: var(--r-cut);
  background: transparent;
  color: var(--text);
  caret-color: var(--accent);
}

.renameInput:focus {
  border-block-end: 2px solid var(--border-active);
  outline: none;
}

.renameSubmit {
  min-block-size: var(--drop);
  padding-inline: var(--s3);
  border: 1px solid var(--border);
  border-radius: var(--r-cut);
  background: var(--bg-raised);
  color: var(--text);
  cursor: pointer;
}

/*
 * The only door to `/history` (F12). A quiet mono line rather than a chip: the sort
 * switch above it already owns this strip's emphasis, and §12.13 allows one loud
 * object per screen — which on the shelf is PLAY ALL, not this.
 */
.historyLink {
  min-block-size: var(--drop);
  padding: 0;
  border: 0;
  background: none;
  color: var(--text-2);
  text-align: left;
  cursor: pointer;
}

.headerActions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--s2);
  min-block-size: var(--drop);
}

/*
 * PLAY ALL — the screen's single inverted block (§8.5, §8.6, §12.13).
 *
 * Flipping ink and paper is the strongest move in the system, so it is spent once per
 * screen and on the primary action. The glyphs on it are `--text-on-paper`, never the
 * other way round (§3.3, §12.9), and the hard `--ink-offset` is a letterpress offset
 * with a zero blur radius, not a shadow (§5.5).
 */
.playAll {
  display: inline-grid;
  place-items: center;
  min-block-size: var(--drop);
  padding-inline: var(--s4);
  border: 0;
  border-radius: var(--r-cut);
  background: var(--bg-paper);
  color: var(--text-on-paper);
  box-shadow: var(--ink-offset);
  cursor: pointer;
}

.playAll:active {
  box-shadow: none;
}

.slug {
  color: var(--text-3);
  overflow-wrap: anywhere;
}

.list {
  display: grid;
  gap: 0;
  margin: 0;
  padding: 0;
  list-style: none;
}

.row {
  display: block;
}

/* The row's tools, revealed under its own hairline — the 1px-ruled action strip of
   §8.5, in the ledger rather than on a sleeve back. */
.tools {
  display: flex;
  flex-wrap: wrap;
  gap: var(--s2);
  align-items: center;
  padding-inline: var(--gutter);
  padding-block: var(--s2);
  border-block-end: 1px solid var(--border);
  background: var(--bg-panel);
}

.toolButton {
  min-inline-size: var(--drop);
  min-block-size: var(--drop);
  padding-inline: var(--s3);
  border: 1px solid var(--border);
  border-radius: var(--r-cut);
  background: transparent;
  color: var(--text);
  cursor: pointer;
}

.toolButton:active {
  background: var(--bg-pressed);
}

.toolButton:disabled {
  color: var(--text-3);
  cursor: default;
}

.toolButton[data-danger='true'] {
  color: var(--danger-text);
}
